import { useEffect, useState } from "react";
import { DateTimeFormatStyle, useI18nContext } from "@hopper-b2b/i18n";
import { TripCategory } from "@overrides/types";
import {
  useFlightSearch,
  CalendarPicker,
  type FlightDateSelectionProps,
} from "@tenant/flights";
import dayjs from "dayjs";

export const useFlightDateSelection = ({
  setCalendarVisitedHandler,
  index,
}: FlightDateSelectionProps) => {
  const [openCalendar, setOpenCalendar] = useState(false);

  const {
    context: {
      tripCategory,
      departureDate: singleRouteDepartureDate,
      returnDate: singleRouteReturnDate,
      multicityRoutes,
    },
  } = useFlightSearch(index);

  const departureDate =
    tripCategory === TripCategory.MULTI_CITY
      ? multicityRoutes[index]?.departureDate
      : singleRouteDepartureDate;

  const returnDate =
    tripCategory === TripCategory.ROUND_TRIP
      ? singleRouteReturnDate
      : undefined;

  const [selectedDepartureDateState, setSelectedDepartureDateState] =
    useState(departureDate);
  const [selectedReturnDateState, setSelectedReturnDateState] =
    useState(returnDate);

  const { t } = useI18nContext();

  useEffect(() => {
    if (departureDate !== selectedDepartureDateState) {
      setSelectedDepartureDateState(departureDate);
    }
    if (returnDate !== selectedReturnDateState) {
      setSelectedReturnDateState(returnDate);
    }
  }, [
    selectedDepartureDateState,
    selectedReturnDateState,
    setSelectedReturnDateState,
    setSelectedDepartureDateState,
    departureDate,
    returnDate,
  ]);

  const minAllowedDateMulticity =
    index > 0
      ? (multicityRoutes[index - 1]?.departureDate ??
        multicityRoutes[0]?.departureDate)
      : undefined;

  const minAllowedDate = dayjs(
    tripCategory === TripCategory.MULTI_CITY
      ? minAllowedDateMulticity
      : undefined
  ).toDate();

  // For multicity flights, max allowed date is the next leg's departure date
  // If this is the last leg, there is no max allowed date
  const nextLegDepartureDate =
    tripCategory === TripCategory.MULTI_CITY &&
    index < multicityRoutes.length - 1
      ? multicityRoutes[index + 1]?.departureDate
      : undefined;

  const maxAllowedDate = nextLegDepartureDate
    ? dayjs(nextLegDepartureDate).toDate()
    : undefined;

  const renderCalendarPopup = () => {
    return (
      <CalendarPicker
        open={openCalendar}
        onClose={() => setOpenCalendar(false)}
        setCalendarVisitedHandler={setCalendarVisitedHandler}
        preventSameDateSelection={false}
        index={index}
        minAllowedDate={minAllowedDate}
        maxAllowedDate={maxAllowedDate}
      />
    );
  };

  return {
    context: {
      startDate: selectedDepartureDateState ?? null,
      startLabel: t("departure"),
      endDate: selectedReturnDateState ?? null,
      endLabel: t("return"),
      dateFormat: DateTimeFormatStyle.ShortMonthDayShortWeekday,
      disabled: false,
      hideEndDate: tripCategory !== TripCategory.ROUND_TRIP,
      minAllowedDate,
      maxAllowedDate,
    },
    handlers: {
      onClick: () => setOpenCalendar(true),
      renderCalendarPopup,
    },
  };
};
